import { Button, Modal } from "react-bootstrap";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import { api_without_cancellation } from "../../../api/api";
import { fetchCriticalNotes } from "../../../Redux/critical-notes/action";
import { formatDateAtTime2, getCaseId, getClientId } from "../../../Utils/helper";
import UserChatBubble2 from "../../Forms/FirmUserBtn2";
import ChatSendingPopup from "../../Forms/common/ChatSendingPopup";

import avatarImage from "./../../../assets/images/avatar.svg";

const CRITICAL_ICON = "https://simplefirm-bucket.s3.amazonaws.com/static/images/critical.svg";

const getErrorMessage = (error, fallback) => {
  const responseData = error?.response?.data;
  if (typeof responseData === "string") return responseData;
  return responseData?.detail || responseData?.message || fallback;
};

const CriticalNotePopup = ({ show, handleClose }) => {
  const dispatch = useDispatch();
  const note = useSelector((state) => state?.criticalNotes?.latestCriticalNote);
  const userOnlineStatus = useSelector((state) => state.chat2.userOnlineStatus);
  const [chatData, setChatData] = useState(null);
  const [chatModal, setChatModal] = useState(false);
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const busy = saving || deleting;
  const normalizedDescription = description.trim();
  const descriptionChanged = normalizedDescription !== (note?.description || "").trim();

  useEffect(() => {
    if (show) setDescription(note?.description || "");
  }, [note?.description, note?.id, show]);

  const refreshCriticalNotes = async () => {
    const clientId = getClientId();
    const caseId = getCaseId();
    if (clientId && caseId) await dispatch(fetchCriticalNotes(clientId, caseId));
  };

  const handleEditCriticalNote = async () => {
    if (!note?.id || !normalizedDescription || !descriptionChanged || busy) return;

    setSaving(true);
    try {
      await api_without_cancellation.patch(`/api/notes-page/notes/${note.id}/`, {
        description: normalizedDescription,
      });
      setDescription(normalizedDescription);
      await refreshCriticalNotes();
      toast.success("Critical note updated successfully.");
      handleClose();
    } catch (error) {
      toast.error(getErrorMessage(error, "Unable to update the critical note."));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCriticalNote = async () => {
    if (!note?.id || busy) return;

    setDeleting(true);
    try {
      await api_without_cancellation.delete(`/api/notes-page/notes/${note.id}/`);
      await refreshCriticalNotes();
      toast.success("Critical note deleted successfully.");
      handleClose();
    } catch (error) {
      toast.error(getErrorMessage(error, "Unable to delete the critical note."));
    } finally {
      setDeleting(false);
    }
  };

  const openChatModel = (event, data) => {
    event?.stopPropagation();
    setChatData(data);
    setChatModal(true);
  };

  return (
    <Modal
      show={show}
      onHide={() => !busy && handleClose()}
      centered
      size="md"
      dialogClassName="modal-dialog-centered"
      contentClassName="custom-modal-new-provider height-400px"
    >
      <div
        className="d-flex align-items-center justify-content-center modal-header p-0"
        style={{
          gap: "5px",
          background: "red",
          minHeight: "25px",
        }}
      >
        <div
          className="font-weight-semibold text-uppercase d-flex align-items-center modal-title h4 cursor-default"
          id="modal_title"
          style={{ fontSize: "14px", fontWeight: 600, color: "white" }}
        >
          Critical Note
        </div>
      </div>

      <Modal.Body className="d-flex flex-column" style={{ gap: "10px", padding: "5px" }}>
        <p
          className="mb-0"
          style={{
            padding: "5px 10px",
            borderLeft: "3px solid red",
            color: "var(--primary)",
            background: "var(--primary-5)",
            fontSize: "14px",
            fontWeight: 600,
          }}
        >
          Critical notes on a case are intended as alerts, warnings or other critical information
          regarding the case and client. Deleting the critical note will remove the warning button
          in the status row.
        </p>

        {note ? (
          <>
            <div className="d-flex justify-content-between align-items-center">
              <span
                className="text-uppercase cursor-default d-flex align-items-center"
                style={{ gap: "5px", fontWeight: 600, color: "#000", fontSize: "14px" }}
              >
                <img src={note?.category?.page_icon || CRITICAL_ICON} alt="" className="ic ic-15" />
                {`${note?.category?.name || ""} Note`}
              </span>
              <div className="d-flex align-items-center" style={{ gap: "10px" }}>
                <span style={{ color: "var(--primary-25)", fontWeight: "600", fontSize: "14px" }}>
                  {formatDateAtTime2(note?.created_at)}
                </span>
                <span className="d-flex align-items-center">
                  <UserChatBubble2
                    first_name={note?.created_by?.first_name ?? ""}
                    last_name={note?.created_by?.last_name ?? ""}
                    profilePic={note?.created_by?.profile_pic ?? ""}
                    is_active={note?.created_by?.is_active}
                    width={19}
                    isActive={userOnlineStatus?.[note?.created_by?.id]?.isOnline ?? false}
                    onClick={(e) => openChatModel(e, note)}
                  />
                </span>
              </div>
            </div>

            <textarea
              aria-label="Edit critical note"
              className="form-control rounded-0"
              value={description}
              disabled={busy}
              rows={6}
              style={{
                minHeight: "125px",
                padding: "5px",
                color: "#000",
                fontSize: "14px",
                fontWeight: 600,
                resize: "vertical",
              }}
              onChange={(event) => setDescription(event.target.value)}
            />
          </>
        ) : (
          <div style={{ color: "#666", fontSize: "14px" }}>No critical note found.</div>
        )}
      </Modal.Body>

      {note && (
        <Modal.Footer
          className="p-0 mt-0 padding-outside-btn-new-provider"
          style={{ gap: "5px", borderTop: "none" }}
        >
          <Button
            variant="secondary"
            className="button-padding-footer-new-provider height-25 d-flex align-items-center justify-content-center"
            disabled={busy}
            onClick={handleClose}
          >
            Close
          </Button>
          <Button
            variant="danger"
            className="button-padding-footer-new-provider height-25 d-flex align-items-center justify-content-center"
            disabled={busy}
            onClick={handleDeleteCriticalNote}
          >
            {deleting ? "Deleting…" : "Delete Critical Note"}
          </Button>
          <Button
            variant="success"
            className="button-padding-footer-new-provider height-25 d-flex align-items-center justify-content-center"
            disabled={busy || !normalizedDescription || !descriptionChanged}
            onClick={handleEditCriticalNote}
          >
            {saving ? "Saving…" : "Edit Critical Note"}
          </Button>
        </Modal.Footer>
      )}

      {chatModal && (
        <ChatSendingPopup
          data={chatData}
          show={chatModal}
          handleClose={() => setChatModal(false)}
          first_name={chatData?.created_by?.first_name}
          last_name={chatData?.created_by?.last_name}
          userId={chatData?.created_by?.id}
          is_active={chatData?.created_by?.is_active}
          profilePic={chatData?.created_by?.profile_pic || avatarImage}
        />
      )}
    </Modal>
  );
};

export default CriticalNotePopup;
